import { useMemo, useState } from 'react';
import { Box, IconButton, Tag, Text } from '@nimbus-ds/components';
import { LinkOffIcon } from '@nimbus-ds/icons';
import { PartnersListItemResponseDto } from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useStoreStatus } from 'domains/Auth/hooks';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import UnlinkPartnerModal from '../../../UnlinkPartnerModal';
import ShowMoreRoles from '../../../UsersListDesktop/components/ShowMoreRoles';

interface PartnersListMobileRowProps {
  partner?: PartnersListItemResponseDto;
  storeRoles: Record<string, string>;
}

function PartnersListMobileRow({
  partner,
  storeRoles,
}: Readonly<PartnersListMobileRowProps>) {
  const t = useTranslationConfigurations('usersListPage.table');
  const [shownRoles, setShownRoles] = useState(7);
  const [isUnlinkPartnerModalOpen, setIsUnlinkPartnerModalOpen] =
    useState(false);
  const { storeStatus } = useStoreStatus();
  const showMoreRoles = () => setShownRoles((prev) => prev + 10);
  const showLessRoles = () => setShownRoles((prev) => prev - 10);

  const filteredPermissions = useMemo(
    () =>
      partner?.permissions?.filter?.((permission) => {
        const label = storeRoles?.[permission];
        return typeof label === 'string' && label.trim().length > 0;
      }),
    [partner?.permissions, storeRoles],
  );

  const showMoreRolesLink =
    filteredPermissions?.length && filteredPermissions?.length > 7;
  const hasMoreRoles = partner?.permissions?.length
    ? partner?.permissions?.length > shownRoles
    : false;

  return (
    <>
      <Box width="100">
        <Stack align="flex-start" gap="none">
          <Stack.Item fill>
            <Stack column align="flex-start" spacing="tight">
              <Text color="neutral-textHigh">
                {partner?.partnerName || t('withoutName')}
              </Text>
              <Text color="neutral-textHigh">{partner?.partnerEmail}</Text>
              <Box display="flex" gap="3">
                <Stack column align="flex-start" spacing="tight">
                  <Stack spacing="tight" wrap>
                    {(filteredPermissions ?? [])
                      .slice(0, shownRoles)
                      .map((permission) => (
                        <Tag
                          key={`partner-${partner?.partnerId}-${permission}`}
                        >
                          <Text fontSize="caption" lineClamp={1}>
                            {storeRoles[permission]}
                          </Text>
                        </Tag>
                      ))}
                  </Stack>
                  {!!showMoreRolesLink && (
                    <ShowMoreRoles
                      hasMoreRoles={hasMoreRoles ?? false}
                      showMoreRoles={showMoreRoles}
                      showLessRoles={showLessRoles}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
          </Stack.Item>
          <Box minWidth="2rem">
            <IconButton
              size="2rem"
              source={<LinkOffIcon />}
              onClick={() => setIsUnlinkPartnerModalOpen(true)}
              disabled={storeStatus === 'development'}
            />
          </Box>
        </Stack>
      </Box>

      <UnlinkPartnerModal
        isOpen={isUnlinkPartnerModalOpen}
        onClose={() => setIsUnlinkPartnerModalOpen(false)}
        partnerName={partner?.partnerName ?? ''}
        partnerEmail={partner?.partnerEmail ?? ''}
      />
    </>
  );
}

export default PartnersListMobileRow;
